import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Calendar, User } from "@phosphor-icons/react";
import Layout, { PageHeader } from "../components/Layout";
import SEO from "../components/SEO";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { Placeholder } from "../components/StatusBadge";
import { api } from "../lib/api";

function fmt(d) {
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
}

export function Insights() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/insights").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <SEO title="Insights" description="Points of view on supply chain execution and EazyMS from the Eazymuv team." />
      <PageHeader eyebrow="Insights" title={<>Points of view on execution</>} subtitle="How we think about closing the execution gap — across operations, product and logistics." />
      <section className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-28">
        {loading ? (
          <div className="text-ez-text/50 font-mono-ez text-sm">Loading…</div>
        ) : (
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ez-text border border-ez-text" data-testid="insights-list">
            {items.map((a) => (
              <StaggerItem key={a.id} className="bg-white">
                <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="group block h-full">
                  <div className="relative h-52 overflow-hidden border-b border-ez-text">
                    {a.featured_image ? (
                      <img src={a.featured_image} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    ) : (
                      <div className="w-full h-full grid-bg bg-ez-bg" />
                    )}
                    <span className="absolute top-3 left-3 bg-ez-yellow text-ez-text px-2.5 py-1 font-mono-ez text-[10px] uppercase tracking-widest">{a.category}</span>
                  </div>
                  <div className="p-6">
                    <div className="font-display text-xl uppercase tracking-tight leading-tight group-hover:text-ez-text/60 transition-colors">{a.title}</div>
                    <p className="mt-3 text-sm text-ez-text/60 line-clamp-3">{a.summary}</p>
                    <div className="mt-5 flex items-center gap-4 font-mono-ez text-[11px] uppercase tracking-wider text-ez-text/40">
                      <span className="inline-flex items-center gap-1"><User size={12} />{a.author}</span>
                      <span className="inline-flex items-center gap-1"><Calendar size={12} />{fmt(a.date)}</span>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        )}
      </section>
    </Layout>
  );
}

export function InsightDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    api.get(`/insights/${slug}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <Layout><div className="max-w-3xl mx-auto px-5 py-32 text-center"><h1 className="font-display text-3xl uppercase">Article not found</h1><Link to="/insights" className="mt-6 inline-block underline">Back to Insights</Link></div></Layout>
  );
  if (!article) return <Layout><div className="max-w-3xl mx-auto px-5 py-32 font-mono-ez text-sm text-ez-text/50">Loading…</div></Layout>;

  return (
    <Layout>
      <SEO title={article.seo_title || article.title} description={article.seo_description || article.summary} image={article.featured_image} />
      <article className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <Link to="/insights" className="inline-flex items-center gap-2 font-mono-ez text-xs uppercase tracking-widest text-ez-text/50 hover:text-ez-text mb-8"><ArrowLeft size={14} /> All Insights</Link>
        <div className="flex items-center gap-3 font-mono-ez text-xs uppercase tracking-widest text-ez-text/50 mb-6">
          <span className="bg-ez-yellow text-ez-text px-2.5 py-1">{article.category}</span>
          <span>{fmt(article.date)}</span>
          <span>· {article.author}</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-[0.95]">{article.title}</h1>
        <p className="mt-6 text-lg text-ez-text/70 leading-relaxed">{article.summary}</p>
        {article.featured_image && (
          <div className="mt-10 border border-ez-text"><img src={article.featured_image} alt={article.title} className="w-full object-cover" /></div>
        )}
        <div className="mt-10"><Placeholder label="Placeholder — final copy to be provided" /></div>
        <div className="mt-6 prose max-w-none text-ez-text/80 leading-relaxed whitespace-pre-line">{article.body}</div>
        <div className="mt-14 border-t border-ez-text pt-8">
          <Link to="/contact" className="inline-flex items-center gap-2 bg-ez-text text-ez-bg px-6 py-3 font-semibold hover:bg-ez-yellow hover:text-ez-text transition-colors">Discuss Your Operations <ArrowUpRight size={16} weight="bold" /></Link>
        </div>
      </article>
    </Layout>
  );
}
